import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "list", "dropzone", "hiddenInputs"]
  static values = {
    url: String,
    existing: { type: Array, default: [] }
  }

  connect() {
    this.files = []
    this.loadExistingFiles()
  }

  loadExistingFiles() {
    for (const file of this.existingValue) {
      this.renderExistingFile(file)
      this.addHiddenInput(file)
      this.files.push(file)
    }
  }

  renderExistingFile(data) {
    const div = document.createElement("div")
    div.id = `file-preview-${data.key}`
    div.className = "flex items-center gap-3 p-2 border rounded-md bg-gray-50"

    if (data.image && data.url) {
      const img = document.createElement("img")
      img.className = "w-12 h-12 object-cover rounded"
      img.src = data.url
      div.appendChild(img)
    } else {
      const icon = document.createElement("div")
      icon.className = "w-12 h-12 flex items-center justify-center bg-gray-200 rounded text-gray-500"
      icon.innerHTML = `<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>`
      div.appendChild(icon)
    }

    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    info.innerHTML = `
      <p class="text-sm font-medium text-gray-700 truncate">${this.escapeHtml(data.filename)}</p>
      <p class="text-xs text-gray-500">${this.escapeHtml(data.file_size)}</p>
    `
    div.appendChild(info)

    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "text-gray-400 hover:text-red-500"
    btn.dataset.action = "click->file-attachment#removeFile"
    btn.dataset.fileAttachmentFileKeyParam = data.key
    btn.innerHTML = `<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>`
    div.appendChild(btn)

    this.listTarget.appendChild(div)
  }

  selectFiles() {
    this.inputTarget.click()
  }

  handleFiles(event) {
    const files = event.target.files
    for (const file of files) {
      this.uploadFile(file)
    }
    this.inputTarget.value = ""
  }

  handleDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-red-400", "bg-red-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-red-400", "bg-red-50")
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-red-400", "bg-red-50")

    const files = event.dataTransfer?.files
    if (!files) return

    for (const file of files) {
      this.uploadFile(file)
    }
  }

  async uploadFile(file) {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`

    this.addFilePreview(tempId, file.name, "업로드 중...", file.type.startsWith("image/") ? file : null, true)

    const formData = new FormData()
    formData.append("file", file)
    const form = this.element.closest("form")
    formData.append("upload_token", form.querySelector('[name="upload_token"]').value)
    form.dataset.pendingUploads = Number(form.dataset.pendingUploads || 0) + 1

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (!response.ok) throw new Error("Upload failed")

      const data = await response.json()
      this.files.push(data)

      this.replaceFilePreview(tempId, data)
      this.addHiddenInput(data)
    } catch (error) {
      this.removeFilePreview(tempId)
      console.error("File upload failed:", error)
    } finally {
      form.dataset.pendingUploads = Math.max(0, Number(form.dataset.pendingUploads || 0) - 1)
    }
  }

  addFilePreview(id, name, sizeText, imageFile, uploading = false) {
    const div = document.createElement("div")
    div.id = `file-preview-${id}`
    div.className = "flex items-center gap-3 p-2 border rounded-md bg-gray-50"

    if (imageFile && typeof imageFile === "object" && imageFile instanceof File) {
      const img = document.createElement("img")
      img.className = "w-12 h-12 object-cover rounded"
      const reader = new FileReader()
      reader.onload = (e) => { img.src = e.target.result }
      reader.readAsDataURL(imageFile)
      div.appendChild(img)
    } else {
      const icon = document.createElement("div")
      icon.className = "w-12 h-12 flex items-center justify-center bg-gray-200 rounded text-gray-500"
      icon.innerHTML = `<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>`
      div.appendChild(icon)
    }

    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    info.innerHTML = `
      <p class="text-sm font-medium text-gray-700 truncate">${this.escapeHtml(name)}</p>
      <p class="text-xs text-gray-500">${this.escapeHtml(sizeText)}</p>
    `
    div.appendChild(info)

    if (uploading) {
      const spinner = document.createElement("div")
      spinner.className = "animate-spin h-5 w-5 border-2 border-red-500 border-t-transparent rounded-full"
      div.appendChild(spinner)
    }

    this.listTarget.appendChild(div)
  }

  replaceFilePreview(tempId, data) {
    const el = document.getElementById(`file-preview-${tempId}`)
    if (!el) return

    el.id = `file-preview-${data.key}`

    const sizeEl = el.querySelector(".text-xs")
    if (sizeEl) sizeEl.textContent = data.file_size

    const spinner = el.querySelector(".animate-spin")
    if (spinner) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "text-gray-400 hover:text-red-500"
      btn.dataset.action = "click->file-attachment#removeFile"
      btn.dataset.fileAttachmentFileKeyParam = data.key
      btn.innerHTML = `<svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>`
      spinner.replaceWith(btn)
    }

    if (data.image && data.url) {
      const img = el.querySelector("img")
      if (img) img.src = data.url
    }
  }

  removeFilePreview(id) {
    const el = document.getElementById(`file-preview-${id}`)
    if (el) el.remove()
  }

  async removeFile(event) {
    const fileKey = event.params.fileKey
    const file = this.files.find(file => file.key === fileKey)
    if (!file) return
    const deleteUrl = file.delete_url || this.urlValue + "/" + file.bf_no

    try {
      const response = await fetch(deleteUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        this.removeFilePreview(fileKey)
        this.removeHiddenInput(fileKey)
        this.files = this.files.filter(f => f.key !== fileKey)
      }
    } catch (error) {
      console.error("File delete failed:", error)
    }
  }

  addHiddenInput(data) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "file_ids[]"
    input.value = data.bf_no
    input.id = `file-input-${data.key}`
    this.hiddenInputsTarget.appendChild(input)
  }

  removeHiddenInput(bfNo) {
    const input = document.getElementById(`file-input-${bfNo}`)
    if (input) input.remove()
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
